/**
 * In-memory staff notification center for the mock API client (tracker item
 * 62). Same rules as the backend where the UI depends on them: ADMIN and
 * OFFICE_MANAGER only (403 otherwise), one alert per recipient, newest first,
 * unread filter, paging (limit at most 50), unread count capped at 100, mark
 * read (404 for an alert that is not the caller's), mark all read with `before`
 * and `type`, per-user mutes (a muted type creates no alert for that user),
 * dedupe by key and coalescing (a repeat re-arms the alert). `raise` is the
 * mock's version of the backend's StaffNotificationService.notify: tests and
 * seeds use it. Not modelled: the real event hooks (the mock does not raise
 * alerts by itself; outside tests, a few sample alerts are seeded per tenant),
 * retention.
 */
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import * as types from './types';

function apiError(status: number, message: string, code?: string): AxiosError {
  const config = { headers: new AxiosHeaders() };
  const response = { status, statusText: '', headers: {}, config, data: { success: false, message, ...(code ? { code } : {}) } } as AxiosResponse;
  return new AxiosError(message, undefined, config, undefined, response);
}

export interface MockStaffNotificationUser {
  id: string;
  tenantId: string;
  role: string;
  isActive?: boolean;
}

export interface MockStaffNotificationHost {
  /** The signed-in user (throws when signed out) */
  currentUser(): MockStaffNotificationUser;
  /** The users of a tenant */
  users(tenantId: string): MockStaffNotificationUser[];
}

export const MOCK_STAFF_NOTIFICATION_TYPES: Record<types.StaffNotificationType, { label: string; description: string }> = {
  SERVICE_REQUEST_RECEIVED: { label: 'New service request', description: 'A customer or website visitor asked for a service.' },
  RESCHEDULE_REQUESTED: { label: 'Reschedule request', description: 'A customer asked to move a visit (or it was moved automatically).' },
  CUSTOMER_REPLY: {
    label: 'Customer reply',
    description: 'A customer wrote back by email or from the portal. One alert per conversation; the Inbox badge counts the same conversations.',
  },
  AUTOPAY_FAILED: { label: 'Failed automatic payment', description: 'An automatic card charge was declined.' },
  PAYMENT_RECEIVED: { label: 'Payment received', description: 'A payment was recorded (online, automatic or entered by another user).' },
  ESTIMATE_APPROVED: { label: 'Estimate approved', description: 'A customer approved an estimate.' },
  ESTIMATE_DECLINED: { label: 'Estimate declined', description: 'A customer declined an estimate.' },
  JOB_COMPLETED: { label: 'Job completed', description: 'A job was marked completed.' },
  SURVEY_LOW_SCORE: { label: 'Low survey score', description: 'A customer rated a visit at or below the low-score threshold (Settings > Surveys).' },
  BUNDLE_RENEWAL_PENDING: { label: 'Bundle renewal awaiting approval', description: 'A renewal of a service bundle was created (by hand, in bulk or automatically) and waits for approval.' },
  BUNDLE_REQUEST_PENDING: { label: 'Bundle request awaiting approval', description: 'A customer asked for a service bundle from the portal and the request waits for approval.' },
  BUNDLE_RENEWAL_BLOCKED: { label: 'Bundle renewal blocked', description: 'An automatic renewal could not be created because the bundle is inactive or deleted, or nothing in it can be scheduled.' },
};
const TYPE_ORDER = Object.keys(MOCK_STAFF_NOTIFICATION_TYPES) as types.StaffNotificationType[];

const BELL_ROLES = ['ADMIN', 'OFFICE_MANAGER'];
const CAP = 100;
const LIST_DEFAULT_LIMIT = 20;
const LIST_MAX_LIMIT = 50;

interface Row extends types.StaffNotification {
  tenantId: string;
  userId: string;
  dedupeKey: string | null;
}

export interface MockRaiseInput {
  title: string;
  body?: string | null;
  link?: types.StaffNotificationLink;
  dedupeKey?: string;
  coalesce?: boolean;
  recipientUserIds?: string[];
  routeByRole?: boolean;
  excludeUserIds?: string[];
  /** Override the creation time (tests) */
  createdAt?: Date;
}

let sequence = 0;

export class MockStaffNotifications {
  private rows: Row[] = [];
  private muted = new Map<string, boolean>(); // `${userId}:${type}`
  private seeded = new Set<string>();

  constructor(private readonly host: MockStaffNotificationHost, private readonly seedSamples = process.env.NODE_ENV !== 'test') {}

  /** Mock version of the backend's notify: route, drop muted and excluded users, dedupe / coalesce, insert. */
  raise(tenantId: string, type: types.StaffNotificationType, input: MockRaiseInput): number {
    const users = this.host.users(tenantId).filter((u) => u.isActive !== false);
    const explicit = users.filter((u) => (input.recipientUserIds ?? []).includes(u.id) && BELL_ROLES.includes(u.role));
    const byRole = users.filter((u) => BELL_ROLES.includes(u.role));
    const chosen = input.routeByRole === false && explicit.length > 0 ? explicit : [...explicit, ...byRole];
    const excluded = new Set(input.excludeUserIds ?? []);
    const seen = new Set<string>();
    let written = 0;
    for (const user of chosen) {
      if (excluded.has(user.id) || seen.has(user.id)) continue;
      seen.add(user.id);
      if (this.muted.get(`${user.id}:${type}`)) continue;
      const key = input.dedupeKey ? `${type}:${input.dedupeKey}` : null;
      const existing = key ? this.rows.find((r) => r.tenantId === tenantId && r.userId === user.id && r.dedupeKey === key) : undefined;
      const createdAt = (input.createdAt ?? new Date()).toISOString();
      if (existing) {
        if (input.coalesce) {
          Object.assign(existing, { title: input.title, body: input.body ?? null, link: input.link ?? null, readAt: null, createdAt });
          written += 1;
        }
        continue;
      }
      this.rows.push({
        id: `sn-${Date.now().toString(36)}-${(++sequence).toString(36)}`,
        tenantId,
        userId: user.id,
        dedupeKey: key,
        type,
        title: input.title,
        body: input.body ?? null,
        link: input.link ?? null,
        readAt: null,
        createdAt,
      });
      written += 1;
    }
    return written;
  }

  private caller(): MockStaffNotificationUser {
    const user = this.host.currentUser();
    if (!BELL_ROLES.includes(user.role)) throw apiError(403, 'Staff notifications are not available for this role', 'FORBIDDEN');
    this.seedFor(user.tenantId);
    return user;
  }

  /** A few sample alerts per tenant for the offline demo (never in tests). */
  private seedFor(tenantId: string): void {
    if (!this.seedSamples || this.seeded.has(tenantId)) return;
    this.seeded.add(tenantId);
    const ago = (minutes: number) => new Date(Date.now() - minutes * 60_000);
    this.raise(tenantId, 'SERVICE_REQUEST_RECEIVED', { title: 'New service request from Olive Oyl', body: 'Mowing - Customer portal', link: { type: 'SERVICE_REQUEST', id: 'sample-request' }, dedupeKey: 'sample-1', createdAt: ago(12) });
    this.raise(tenantId, 'CUSTOMER_REPLY', { title: 'Popeye replied', body: 'Gate code (email)', link: { type: 'CONVERSATION', id: 'sample-conversation' }, dedupeKey: 'sample-2', coalesce: true, createdAt: ago(55) });
    this.raise(tenantId, 'PAYMENT_RECEIVED', { title: 'Payment of $125.00 received from Wimpy', link: { type: 'PAYMENT', id: 'sample-payment' }, dedupeKey: 'sample-3', createdAt: ago(60 * 26) });
  }

  private mine(user: MockStaffNotificationUser): Row[] {
    return this.rows
      .filter((r) => r.tenantId === user.tenantId && r.userId === user.id)
      .sort((a, b) => (a.createdAt === b.createdAt ? (a.id < b.id ? 1 : -1) : a.createdAt < b.createdAt ? 1 : -1));
  }

  private parseType(value: unknown): types.StaffNotificationType | null {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value !== 'string' || !(value in MOCK_STAFF_NOTIFICATION_TYPES)) {
      throw apiError(400, 'Unknown alert type', 'STAFF_NOTIFICATION_UNKNOWN_TYPE');
    }
    return value as types.StaffNotificationType;
  }

  list(query: types.StaffNotificationListQuery = {}): types.StaffNotificationPage {
    const user = this.caller();
    const type = this.parseType(query.type);
    const page = query.page ?? 1;
    const limit = Math.min(query.limit ?? LIST_DEFAULT_LIMIT, LIST_MAX_LIMIT);
    if (!Number.isInteger(page) || page < 1 || !Number.isInteger(limit) || limit < 1) throw apiError(400, 'page and limit must be positive whole numbers', 'VALIDATION_ERROR');
    const all = this.mine(user);
    const filtered = all.filter((r) => (!query.unread || r.readAt === null) && (!type || r.type === type));
    const data = filtered.slice((page - 1) * limit, page * limit).map(({ tenantId: _t, userId: _u, dedupeKey: _d, ...rest }) => ({ ...rest }));
    return { data, total: filtered.length, page, limit, unread: Math.min(all.filter((r) => r.readAt === null).length, CAP) };
  }

  unreadCount(): types.StaffNotificationUnreadCount {
    const unread = Math.min(this.mine(this.caller()).filter((r) => r.readAt === null).length, CAP);
    return { unread, capped: unread >= CAP };
  }

  markRead(id: string): { id: string; read: true } {
    const row = this.mine(this.caller()).find((r) => r.id === id);
    if (!row) throw apiError(404, 'Notification not found', 'NOT_FOUND');
    if (!row.readAt) row.readAt = new Date().toISOString();
    return { id, read: true };
  }

  markAllRead(input: types.StaffNotificationMarkAllInput = {}): { updated: number } {
    const user = this.caller();
    const type = this.parseType(input.type);
    let before: number | null = null;
    if (input.before !== undefined) {
      before = Date.parse(input.before);
      if (Number.isNaN(before)) throw apiError(400, 'before must be an ISO date-time', 'VALIDATION_ERROR');
    }
    let updated = 0;
    const now = new Date().toISOString();
    for (const row of this.mine(user)) {
      if (row.readAt || (type && row.type !== type) || (before !== null && Date.parse(row.createdAt) > before)) continue;
      row.readAt = now;
      updated += 1;
    }
    return { updated };
  }

  preferences(): types.StaffNotificationPreferences {
    const user = this.caller();
    return {
      types: TYPE_ORDER.map((type) => ({ type, ...MOCK_STAFF_NOTIFICATION_TYPES[type], muted: this.muted.get(`${user.id}:${type}`) === true })),
    };
  }

  setPreferences(muted: Partial<Record<types.StaffNotificationType, boolean>>): types.StaffNotificationPreferences {
    const user = this.caller();
    if (!muted || typeof muted !== 'object' || Array.isArray(muted)) throw apiError(400, 'muted must be an object of alert type to true / false', 'VALIDATION_ERROR');
    for (const [type, value] of Object.entries(muted)) {
      if (!(type in MOCK_STAFF_NOTIFICATION_TYPES)) throw apiError(400, `Unknown alert type: ${type}`, 'STAFF_NOTIFICATION_UNKNOWN_TYPE');
      if (typeof value !== 'boolean') throw apiError(400, `${type} must be true or false`, 'VALIDATION_ERROR');
    }
    for (const [type, value] of Object.entries(muted)) this.muted.set(`${user.id}:${type}`, value as boolean);
    return this.preferences();
  }
}
